import { db } from "../connection";
import { applicationError } from "../../../redux/actions/error";
import validator from "validator";
import {
  arrayUnion,
  collection,
  doc,
  getDocs,
  limit,
  query,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import logger from "../../../utils/logger";

// Create or invite an admin for a group.
export async function fbCreateAdminForGroup(email, groupId, data) {
  if (!validator.isEmail(email || "")) {
    throw {
      message: `${email} is not a valid email, provide a valid email for admin`,
    };
  }

  const normalizedEmail = email.trim().toLowerCase();
  const adminRole = data?.type || "admin";

  try {
    // Check if user is already a member of this group (by email)
    const membersRef = collection(db, "userGroup", groupId, "members");
    const memberQuery = query(
      membersRef,
      where("email", "==", normalizedEmail),
      limit(1),
    );
    const memberSnap = await getDocs(memberQuery);

    if (!memberSnap.empty) {
      const memberDocId = memberSnap.docs[0].id;
      try {
        await fbUpdateUserToAdmin(memberDocId, groupId, adminRole);
      } catch (err) {
        logger.error("Error updating user to admin", err);
        applicationError({
          message: "There was an error updating user to admin",
          err,
          intensity: "low",
          errorCode: "firebase-modal",
        });
      }
      return;
    }

    // User not in group: invite_emails/{groupId} doc has emailList; userDetails subcollection has one doc per user
    const userDetailsRef = collection(
      db,
      "invite_emails",
      groupId,
      "userDetails",
    );
    const existingQuery = query(
      userDetailsRef,
      where("email", "==", normalizedEmail),
      limit(1),
    );
    const existingSnap = await getDocs(existingQuery);

    if (!existingSnap.empty) {
      const existingDocRef = existingSnap.docs[0].ref;
      await updateDoc(existingDocRef, {
        name: data?.name ?? "",
        userRole: adminRole,
      });
      return;
    }

    await fbAddToInviteList(groupId, normalizedEmail);
    const newUserRef = doc(userDetailsRef);
    await setDoc(newUserRef, {
      email: normalizedEmail,
      name: data?.name ?? "",
      userRole: adminRole,
    });
  } catch (err) {
    const message = "There was an error adding admin invite. Please try again.";
    logger.error("fbCreateAdminForGroup error", err);
    applicationError({
      message,
      err,
      intensity: "high",
      show: false,
      errorCode: "firebase-modal",
    });
    throw { message };
  }
}

// Add email to invite_emails.{groupId}.emails
export async function fbAddToInviteList(groupId, email) {
  const ref = doc(db, "invite_emails", groupId);

  try {
    await setDoc(ref, { emails: arrayUnion(email) }, { merge: true });
  } catch (err) {
    logger.error(err);
    const message =
      "Unable to add email to the invite list, contact chant developer";
    applicationError({
      message,
      err,
      intensity: "high",
      errorCode: "firebase-modal",
    });
    throw { message };
  }
}

// Promote existing userGroup member to admin/parentAdmin.
export function fbUpdateUserToAdmin(userGroupId, groupId, type) {
  return new Promise((resolve, reject) => {
    setDoc(
      doc(db, "userGroup", groupId, "members", userGroupId),
      { userRole: type },
      { merge: true },
    )
      .then(() => {
        resolve();
      })
      .catch((err) => {
        logger.error(err);
        const message =
          "Unable to update the user as admin to the group, contact chant developer";
        applicationError({
          message,
          err,
          intensity: "high",
          errorCode: "firebase-modal",
        });
        reject({ message });
      });
  });
}

// Remove admin role from an invited user: set userRole to "member" in invite_emails userDetails.
// Does not remove entry from invite_emails.
export async function fbRemoveAdminForGroup(email, groupId) {
  try {
    const normalizedEmail = (email || "").trim().toLowerCase();
    const userDetailsRef = collection(
      db,
      "invite_emails",
      groupId,
      "userDetails",
    );
    const q = query(
      userDetailsRef,
      where("email", "==", normalizedEmail),
      limit(1),
    );
    const snap = await getDocs(q);

    if (snap.empty) {
      return;
    }

    await updateDoc(snap.docs[0].ref, { userRole: "member" });
  } catch (err) {
    const message = "Unable to remove the admin from the group, contact admin";
    applicationError({
      message,
      err,
      intensity: "high",
      errorCode: "firebase-modal",
    });
    throw { message };
  }
}

// Demote registered admin (userGroup member) back to member.
export function fbDemoteAdminForGroup(groupId, userGroupId) {
  return new Promise((resolve, reject) => {
    setDoc(
      doc(db, "userGroup", groupId, "members", userGroupId),
      { userRole: "member" },
      { merge: true },
    )
      .then(() => {
        resolve();
      })
      .catch((err) => {
        const message =
          "Unable to demote the admin from the group, contact chant developer";
        applicationError({
          message,
          err,
          intensity: "high",
          errorCode: "firebase-modal",
        });
        reject({ message });
      });
  });
}

// Change role for a registered admin/member.
export function fbChangeAdminRole(role, groupId, userGroupId) {
  return new Promise((resolve, reject) => {
    setDoc(
      doc(db, "userGroup", groupId, "members", userGroupId),
      { userRole: role },
      { merge: true },
    )
      .then(() => {
        resolve();
      })
      .catch((err) => {
        const message =
          "Unable to change role for the admin, contact chant developer";
        applicationError({
          message,
          err,
          intensity: "high",
          errorCode: "firebase-modal",
        });
        reject({ message });
      });
  });
}

// Fetch registered and unregistered admins for a group.
// Registered admins come from userGroup members with admin roles.
// Unregistered admins come from invite_emails userDetails with admin roles.
export function fbGetAdminsForGroup(groupId) {
  return new Promise((resolve) => {
    let registeredAdmins = [];
    let unRegisteredAdmins = [];

    const promise1 = getDocs(
      query(
        collection(db, "userGroup", groupId, "members"),
        where("userRole", "in", ["admin", "parentAdmin"]),
      ),
    )
      .then((querySnapshot) => {
        if (querySnapshot.docs.length > 0) {
          querySnapshot.docs.forEach((docSnap) => {
            let data = docSnap.data();
            data.id = docSnap.id;
            registeredAdmins.push(data);
          });
        }
        return registeredAdmins;
      })
      .catch(() => {
        return [];
      });

    const promise2 = getDocs(
      query(
        collection(db, "invite_emails", groupId, "userDetails"),
        where("userRole", "in", ["admin", "parentAdmin"]),
      ),
    )
      .then((querySnapshot) => {
        if (querySnapshot.docs.length > 0) {
          querySnapshot.docs.forEach((docSnap) => {
            const data = docSnap.data();
            unRegisteredAdmins.push({
              id: data.email,
              name:
                data.name && data.name.trim() ? `(pre) ${data.name}` : "(pre)",
              email: data.email,
              userRole: data.userRole,
            });
          });
        }
        return unRegisteredAdmins;
      })
      .catch(() => {
        return [];
      });

    Promise.all([promise1, promise2]).then(([registered, unregistered]) => {
      resolve({
        registeredAdmins: registered,
        unRegisteredAdmins: unregistered,
      });
    });
  });
}
